/**
 * Challenge Progress Module
 * Licznik postępu dynamicznego wyzwania. Konfiguracja przez data-*:
 *
 * data-challenge-current  - liczba dodanych kodów (domyślnie 0)
 * data-challenge-total    - cel (wymagany, np. 20)
 * data-challenge-end      - data końca wyzwania (ISO, np. "2026-06-30T23:59:59") → „Koniec wyzwania za: N dni”
 * data-challenge-counter-template - szablon licznika, domyślnie "{current} / {total} dodanych kodów"
 *
 * Stany success/ended ustawia backend klasą modyfikatora (.challenge-progress--success / --ended)
 * i tekstem w .challenge-progress__status — JS nie nadpisuje statusu w tych stanach.
 *
 * API: window.ChallengeProgress.update(el, current) — aktualizacja licznika (np. po dodaniu kodu).
 *
 * Tooltip „Dynamiczne bonusy” (Figma 3897:149228): przycisk [data-challenge-help] otwiera wspólny
 * popover `alert-banner--popover` (tryb detached, logika w alert-banner.js: X / klik poza / Esc).
 * Treść można nadpisać atrybutami data-challenge-help-title / data-challenge-help-text na przycisku.
 */
(function () {
  'use strict';

  var SELECTOR = '.challenge-progress[data-challenge-total]';
  var DAY_MS = 24 * 60 * 60 * 1000;

  function pluralDays(n) {
    return n === 1 ? 'dzień' : 'dni';
  }

  function render(el, animate) {
    var total = parseInt(el.getAttribute('data-challenge-total'), 10) || 1;
    var current = Math.min(parseInt(el.getAttribute('data-challenge-current'), 10) || 0, total);
    var template = el.getAttribute('data-challenge-counter-template') || '{current} / {total} dodanych kodów';
    var percent = (current / total) * 100;

    var counter = el.querySelector('.challenge-progress__counter');
    if (counter) {
      counter.textContent = template.replace('{current}', current).replace('{total}', total);
    }

    var isFinal = el.classList.contains('challenge-progress--success') ||
      el.classList.contains('challenge-progress--ended');

    var end = el.getAttribute('data-challenge-end');
    var status = el.querySelector('.challenge-progress__status');
    if (end && status && !isFinal) {
      var days = Math.max(0, Math.ceil((new Date(end).getTime() - Date.now()) / DAY_MS));
      status.textContent = 'Koniec wyzwania za: ' + days + ' ' + pluralDays(days);
    }

    if (animate) {
      el.style.setProperty('--challenge-progress', percent + '%');
    } else {
      el.dataset.challengePercent = percent;
    }
  }

  function reveal(el) {
    el.style.setProperty('--challenge-progress', (el.dataset.challengePercent || 0) + '%');
  }

  var POPOVER_ID = 'challenge-help-popover';
  var HELP_TITLE = 'Dynamiczne bonusy';
  var HELP_TEXT = 'To wyzwania z limitem czasu, które pojawiają się cyklicznie. Ukończ wyzwanie przed końcem odliczania i zdobądź dodatkowe punkty Camel.';

  function createPopover() {
    var popover = document.createElement('div');
    popover.className = 'alert-banner alert-banner--default alert-banner--sm alert-banner--popover challenge-progress__popover';
    popover.id = POPOVER_ID;
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-labelledby', POPOVER_ID + '-title');
    popover.style.display = 'none';
    popover.innerHTML =
      '<span class="alert-banner__icon" aria-hidden="true"></span>' +
      '<div class="alert-banner__content">' +
      '<p class="alert-banner__title" id="' + POPOVER_ID + '-title"></p>' +
      '<p class="alert-banner__text"></p>' +
      '</div>' +
      '<button class="alert-banner__close" type="button" aria-label="Zamknij"></button>';
    document.body.appendChild(popover);
    return popover;
  }

  function initHelp() {
    var buttons = document.querySelectorAll('[data-challenge-help]');
    if (!buttons.length) return;

    var popover = document.getElementById(POPOVER_ID) || createPopover();
    var titleEl = popover.querySelector('.alert-banner__title');
    var textEl = popover.querySelector('.alert-banner__text');

    buttons.forEach(function (btn) {
      btn.setAttribute('data-popover-trigger', POPOVER_ID);
      btn.setAttribute('aria-controls', POPOVER_ID);
      btn.setAttribute('aria-expanded', 'false');
      // Treść ustawiana przed toggle (capture — przed delegacją w alert-banner.js)
      btn.addEventListener('click', function () {
        titleEl.textContent = btn.getAttribute('data-challenge-help-title') || HELP_TITLE;
        textEl.textContent = btn.getAttribute('data-challenge-help-text') || HELP_TEXT;
      }, true);
    });

    // Synchronizacja aria-expanded ze stanem popovera
    new MutationObserver(function () {
      var open = popover.classList.contains('is-visible') && !popover.classList.contains('is-dismissing');
      buttons.forEach(function (btn) {
        btn.setAttribute('aria-expanded', open && popover._popoverTrigger === btn ? 'true' : 'false');
      });
    }).observe(popover, { attributes: true, attributeFilter: ['class'] });

    // Popover ma position: fixed — przy przewijaniu podąża za przyciskiem (odstęp 8px jak w alert-banner.js)
    window.addEventListener('scroll', function () {
      var trigger = popover._popoverTrigger;
      if (!trigger || !popover.classList.contains('is-visible')) return;
      var rect = trigger.getBoundingClientRect();
      if (popover.classList.contains('alert-banner--popover-top')) {
        popover.style.bottom = (window.innerHeight - rect.top + 8) + 'px';
      } else {
        popover.style.top = (rect.bottom + 8) + 'px';
      }
    }, { passive: true });
  }

  function init() {
    initHelp();

    var elements = document.querySelectorAll(SELECTOR);
    if (!elements.length) return;

    var observer = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 }) : null;

    elements.forEach(function (el) {
      render(el, false);
      if (observer) {
        observer.observe(el);
      } else {
        reveal(el);
      }
    });
  }

  window.ChallengeProgress = {
    update: function (el, current) {
      if (!el) return;
      el.setAttribute('data-challenge-current', current);
      render(el, true);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
